import React, { memo } from 'react';
import { SidebarBoundary } from '@atlassian/jira-navigation-apps-sidebar-common/src/async.tsx';
import {
	setPerformanceMarkLoadStart,
	setPerformanceMarkLoadEnd,
} from '@atlassian/jira-navigation-apps-sidebar-common/src/utils/performance-marks/index.tsx';
import { lazyAfterPaint, lazyForPaint } from '@atlassian/react-loosely-lazy';
import { useIsActiveRoute } from '@atlassian/react-resource-router';
import type { SidebarClassicSoftware as ClassicSoftwareProjectSidebarType } from './ui/index.tsx';

const LazyAfterPaintClassicSoftwareProjectSidebar = lazyAfterPaint<
	typeof ClassicSoftwareProjectSidebarType
>(
	() => {
		setPerformanceMarkLoadStart();

		return import(/* webpackChunkName: "async-sidebar-software-classic" */ './index').then(
			(bundle) => {
				setPerformanceMarkLoadEnd();
				return bundle;
			},
		);
	},
	{ entryFsSsrLiftImportToModule: false },
);

const LazyForPaintClassicSoftwareProjectSidebar = lazyForPaint<
	typeof ClassicSoftwareProjectSidebarType
>(
	() => {
		setPerformanceMarkLoadStart();

		// eslint-disable-next-line jira/import/webpack-magic-comment-entries
		return import(/* webpackChunkName: "async-sidebar-software-classic" */ './index').then(
			(bundle) => {
				setPerformanceMarkLoadEnd();
				return bundle;
			},
		);
	},
	{
		includeInShellOnlySsrBundle: true,
		entryFsSsrLiftImportToModule: false,
	},
);

const AfterPaintClassicSoftwareProjectSidebar = memo(() => (
	<SidebarBoundary id="classicSoftwareProject">
		<LazyAfterPaintClassicSoftwareProjectSidebar />
	</SidebarBoundary>
));

const ForPaintClassicSoftwareProjectSidebar = memo(() => (
	<SidebarBoundary id="classicSoftwareProject">
		<LazyForPaintClassicSoftwareProjectSidebar />
	</SidebarBoundary>
));

const ClassicSoftwareProjectSidebar = memo(() => {
	const isIssueRoute = useIsActiveRoute('issue');
	return isIssueRoute ? (
		<ForPaintClassicSoftwareProjectSidebar />
	) : (
		<AfterPaintClassicSoftwareProjectSidebar />
	);
});

ClassicSoftwareProjectSidebar.displayName = 'ClassicSoftwareProjectSidebar';
ForPaintClassicSoftwareProjectSidebar.displayName = 'ClassicSoftwareProjectSidebar';

export { ForPaintClassicSoftwareProjectSidebar };
export default ClassicSoftwareProjectSidebar;
